import { useMemo } from 'react';
import { useI18n } from '@/i18n';
import { type CountUnit, countText } from '@/i18n/count';
import type { Currency, IsoDate, IsoMonth, Money, Timestamp } from '@/lib/api/types';
import { useOptionalApp } from '@/lib/auth/app-context';
import { type DateStyle, formatDate, formatDateTime, formatMonth, formatTime, formatWeekday, todayIn } from './date';
import { CURRENCY_DECIMALS, currencySymbol, type Dec, formatMoney, formatNumber, type MoneyFormatOptions, numberLocale } from './money';

export interface Formatter {
  lang: 'ar' | 'en';
  /** Locale for numbers and dates: the school's (ar-IQ / ar-LY) in Arabic, en-GB in English. */
  locale: string;
  currency: Currency;
  timeZone: string;
  symbol: string;
  decimals: number;
  money: (value: Money | Dec | null | undefined, opts?: MoneyFormatOptions & { currency?: Currency }) => string;
  number: (value: number | string, maxFraction?: number) => string;
  percent: (value: number) => string;
  date: (value: IsoDate | Timestamp | null | undefined, style?: DateStyle) => string;
  month: (value: IsoMonth | IsoDate | Timestamp | null | undefined) => string;
  dateTime: (value: Timestamp | null | undefined, style?: DateStyle) => string;
  time: (value: Timestamp | null | undefined) => string;
  weekday: (value: IsoDate) => string;
  today: () => IsoDate;
  /** Free text with ASCII digits ("2026–2027") in the locale's digits ("٢٠٢٦–٢٠٢٧" in ar-IQ). */
  digits: (text: string) => string;
  /** "٨ أشهر", "شهرين", "12 months": a number with its noun in the right grammatical number. */
  count: (n: number | string, unit: CountUnit, maxFraction?: number) => string;
}

export function makeFormatter(lang: 'ar' | 'en', school: { locale: string; currency: Currency; timeZone: string } | null): Formatter {
  // Without a school (platform console) Arabic uses plain "ar" (Latin digits).
  const locale = lang === 'en' ? 'en-GB' : (school?.locale ?? 'ar');
  const currency = school?.currency ?? 'IQD';
  const timeZone = school?.timeZone ?? 'Asia/Baghdad';
  // Numbers follow the school's number rules (Libya: 1,250.500), dates its locale.
  const numbers = numberLocale(locale);
  const percentFormat = new Intl.NumberFormat(numbers, { style: 'percent', maximumFractionDigits: 1 });
  const glyphs = Array.from({ length: 10 }, (_, d) => new Intl.NumberFormat(numbers, { useGrouping: false }).format(d));
  return {
    lang,
    locale,
    currency,
    timeZone,
    symbol: currencySymbol(currency, lang),
    decimals: CURRENCY_DECIMALS[currency],
    money: (value, opts = {}) => formatMoney(value, { locale, currency: opts.currency ?? currency, lang }, opts),
    number: (value, maxFraction = 0) => formatNumber(value, locale, maxFraction),
    percent: (value) => percentFormat.format(value / 100),
    date: (value, style) => formatDate(value, locale, style),
    month: (value) => formatMonth(value, locale),
    dateTime: (value, style) => formatDateTime(value, locale, timeZone, style),
    time: (value) => formatTime(value, locale, timeZone),
    weekday: (value) => formatWeekday(value, locale),
    today: () => todayIn(timeZone),
    digits: (text) => text.replace(/[0-9]/g, (d) => glyphs[Number(d)]!),
    count: (n, unit, maxFraction = 1) => countText(lang, Number(n), unit, formatNumber(n, locale, maxFraction)),
  };
}

export function useFormat(): Formatter {
  const { lang } = useI18n();
  const school = useOptionalApp()?.school ?? null;
  const locale = school?.locale;
  const currency = school?.currency;
  const timeZone = school?.timeZone;
  return useMemo(
    () => makeFormatter(lang, locale && currency && timeZone ? { locale, currency, timeZone } : null),
    [lang, locale, currency, timeZone],
  );
}
